<template>
  <v-layout row justify-center>
    <v-dialog v-model="editorDialog" fullscreen transition="dialog-bottom-transition" :overlay=false>
      <v-card>
        <v-toolbar dark color="primary white--text">
          <v-toolbar-title class="mr-3">ADI XML</v-toolbar-title>
          <v-list class="primary">
            <v-list-tile class="white--text">
              <v-list-tile-content>
                <v-list-tile-sub-title class="white--text">Title</v-list-tile-sub-title>
                <v-list-tile-title>{{ assetMetadata.title }}</v-list-tile-title>
              </v-list-tile-content>
              <v-list-tile-content>
                <v-list-tile-sub-title class="white--text">Version</v-list-tile-sub-title>
                <v-list-tile-title>{{ getVersionNumber(assetMetadata) }}</v-list-tile-title>
              </v-list-tile-content>
              <v-list-tile-content>
                <v-list-tile-sub-title class="white--text">Provider</v-list-tile-sub-title>
                <v-list-tile-title>{{ assetMetadata.providerName }}</v-list-tile-title>
              </v-list-tile-content>
              <v-list-tile-content>
                <v-list-tile-sub-title class="white--text">Destination Platform</v-list-tile-sub-title>
                <v-list-tile-title>{{ assetMetadata.destinationPlatform }}</v-list-tile-title>
              </v-list-tile-content>
            </v-list-tile>
          </v-list>
          <v-spacer></v-spacer>
          <v-toolbar-items>
            <v-btn @click.stop="refreshSearchResult" flat>
              <v-icon class="white--text">close</v-icon>
            </v-btn>
          </v-toolbar-items>
        </v-toolbar>
        <v-card-text>
          <v-stepper v-model="editingStep">
            <v-stepper-header>
              <v-stepper-step step="1" :complete="editingStep > 1" :editable="stepsEditable">
                {{ messages.label.EDIT }}
              </v-stepper-step>
              <v-divider></v-divider>
              <v-stepper-step step="2" :complete="editingStep > 2" :editable="stepsEditable">
                {{ messages.label.REVIEW }}
              </v-stepper-step>
              <v-divider></v-divider>
              <v-stepper-step step="3" v-bind:rules="[() => !didSubmitFail]">
                <span v-if="didSubmitFail">
                  {{ messages.label.ERROR }}
                </span>
                <span v-else>
                  {{ messages.label.COMPLETE }}
                </span>
              </v-stepper-step>
            </v-stepper-header>
            <v-stepper-items>
              <!-- STEP 1 -->
              <v-stepper-content step="1">
                <v-alert :value="loadXMLFailure" color="warning" icon="warning">
                  {{ loadXMLErrorMsg }}
                  <v-btn @click="reloadXML" fab dark small v-if="isXMLStale" color="primary">
                    <v-icon dark>refresh</v-icon>
                  </v-btn>
                </v-alert>
                <v-card :height="editorHeight">
                  <div ref="editorEditable" class="vop--ace-editor"></div>
                </v-card>
                <v-btn color="primary" v-bind:disabled="!canPreview" @click="displayDifferences">
                  {{ messages.label.CONTINUE }}
                </v-btn>
                <v-btn color="grey lighten-1 white--text" @click.stop="editorDialog = false">
                  {{ messages.label.CANCEL }}
                </v-btn>
              </v-stepper-content>
              <!-- STEP 2 -->
              <v-stepper-content step="2">
                <v-progress-linear v-if="submittingXML" v-bind:indeterminate="true"></v-progress-linear>
                <v-card :height="editorHeight">
                  <div class="vop--acediff-editor-container">
                    <div>
                      <div class="vop--acediff-editor" ref="diffEditorLeft">
                      </div>
                    </div>
                    <div class="vop--acediff-gutter" ref="diffGutter"></div>
                    <div>
                      <div class="vop--acediff-editor" ref="diffEditorRight">
                      </div>
                    </div>
                  </div>
                </v-card>
                <v-btn color="primary" :loading="submittingXML" :disabled="submittingXML" @click="submitXML">
                  {{ messages.label.SUBMIT }}
                </v-btn>
                <v-btn color="grey lighten-1 white--text" @click.stop="editorDialog = false">
                  {{ messages.label.CANCEL }}
                </v-btn>
              </v-stepper-content>
              <!-- STEP 3 -->
              <v-stepper-content step="3">
                <v-card>
                  <v-alert color="success" icon="check_circle" value="true" v-if="xmlSubmitSuccess">
                    <span v-html="xmlSubmitResponseMsg"></span>
                  </v-alert>
                  <v-alert color="error" icon="warning" value="true" v-else>
                    <span v-html="xmlSubmitResponseMsg"></span>
                  </v-alert>
                </v-card>
                <v-btn color="primary" v-if="xmlSubmitSuccess" @click="refreshSearchResult">
                  {{ messages.label.DONE }}
                </v-btn>
                <v-btn color="primary" v-else @click="refreshSearchResult">
                  {{ messages.label.CLOSE }}
                </v-btn>
              </v-stepper-content>
            </v-stepper-items>
          </v-stepper>
        </v-card-text>
      </v-card>
    </v-dialog>
  </v-layout>
</template>

<script>
import ace from "brace"
import axios from "axios"
import AceDiff from "brace-diff"
import "brace/mode/xml"
import "brace/theme/eclipse"
import "brace/ext/searchbox"
import XMLBacklogService from "@/XMLBacklog/XMLBacklogService"
import * as utils from "@/Shared/utils"
import messages from "@/XMLBacklog/messages.json"

export default {
  name: "XMLBacklogMetadataEdit",
  data() {
    return {
      editorDialog: false,
      assetMetadata: {},
      originalXML: "",
      canPreview: false,
      editingStep: 0,
      stepsEditable: false,
      isXMLStale: true,
      loadXMLFailure: false,
      submittingXML: false,
      xmlSubmitSuccess: false,
      loadXMLErrorMsg: "",
      modifiedXML: "",
      selectedXMLUID: "",
      xmlSubmitResponseMsg: ""
    }
  },
  methods: {
    /**
     * Retrieve Metadata for given XML UID
     * httpStatus                statusCode statusMessage                         message
     * ------------------------- ---------- ------------------------------------- ---------------------------
     *  200 OK
     *  404 Not Found             1001300  Asset not found
     *  412 PRECONDITION_FAILED   1001400  Asset is stale
     *  500 INTERNAL_SERVER_ERROR 1000100  Error                                   (various errors)
     *  503 SERVICE_UNAVAILABLE   1001100  Error accessing database                (various DB errors)
     *  503 SERVICE_UNAVAILABLE   1000700  Unable to access system                 (various network errors)
     */
    retrieveAssetMetadata(xmlUID, reload) {
      let assetMetadataURI =
        utils.getServerUrl() + "/api/xml-backlog/asset"
      assetMetadataURI += "/" + xmlUID
      axios
        .get(assetMetadataURI)
        .then((response) => {
          this.assetMetadata = response.data.data.assetMetadata
          this.originalXML = response.data.data.xml
          this.isXMLStale = false
          this.loadXMLFailure = false
          this.loadXMLErrorMsg = ""
          if (reload) {
            XMLBacklogService.$emit(
              "event:updateStaleXMLRow",
              this.assetMetadata
            )
          }
          this.openXMLEditor()
        })
        .catch((error) => {
          let response = error.response.data
          const statusCode = response.statusCode
          this.loadXMLFailure = true
          switch (statusCode) {
            case 1001400:
              this.isXMLStale = true
              this.loadXMLErrorMsg = messages.error.STALE_XML
              this.assetMetadata = response.data.assetMetadata
              this.originalXML = response.data.xml
              this.openXMLEditor()
              break
            case 1001300:
              this.isXMLStale = false
              this.loadXMLErrorMsg = messages.error.XML_NOT_FOUND
              this.assetMetadata = response.data.assetMetadata
              this.originalXML = ""
              this.openXMLEditor()
              break
            default:
              this.isXMLStale = false
              this.loadXMLErrorMsg = response.data.statusMessage
              this.assetMetadata = response.data.assetMetadata
              this.originalXML = ""
              this.openXMLEditor()
          }
        })
    },
    /**
     * Check if XML is in valid state, if not disable 'CONTINUE' button
     */
    validateXML(event, editor) {
      let xmlSyntaxError = false
      let annotationLists = editor.getSession().getAnnotations()
      for (var i in annotationLists) {
        let annotationError = annotationLists[i].type
        if (annotationError === "error" || annotationError === "warning") {
          xmlSyntaxError = true
          break
        }
      }
      if (this.isXMLStale || xmlSyntaxError || this.loadXMLFailure) {
        this.canPreview = false
      } else {
        this.canPreview = true
      }
    },
    /**
     * Open XML Editor dialog
     */
    openXMLEditor() {
      let editor = ace.edit(this.$refs.editorEditable)
      this.editorDialog = true
      this.stepsEditable = false
      this.editingStep = 1
      editor.setTheme("ace/theme/eclipse")
      editor.getSession().setMode("ace/mode/xml")
      editor.session.setValue(this.originalXML)
      editor.getSession().on("changeAnnotation", (event) => {
        this.validateXML(event, editor)
      })
    },
    /**
     * Display differences between original and modified version
     */
    displayDifferences() {
      let editor = ace.edit(this.$refs.editorEditable)
      this.editingStep = 2
      this.stepsEditable = true
      this.modifiedXML = editor.getValue()
      let aceDiffer = new AceDiff({
        mode: "ace/mode/xml",
        showDiffs: true,
        showConnectors: true,
        gutterEl: this.$refs.diffGutter,
        left: {
          element: this.$refs.diffEditorLeft,
          theme: "ace/theme/eclipse",
          editable: false,
          copyLinkEnabled: false
        },
        right: {
          element: this.$refs.diffEditorRight,
          theme: "ace/theme/eclipse",
          editable: false,
          copyLinkEnabled: false
        }
      })
      let editors = aceDiffer.getEditors()
      // Set Value to the editors
      editors.left.setValue(this.originalXML, 1)
      editors.right.setValue(this.modifiedXML, 1)
    },
    /**
     * Submit XML Changes
     * httpStatus                statusCode statusMessage                         message
     *  ------------------------- ---------- ------------------------------------- ---------------------------
     *  200 OK                    0
     *  400 BAD_REQUEST           1001600  Asset data mismatch
     *  400 BAD_REQUEST           1000900  Validation error.                       Unable to update - newer version of Asset in DB
     *  400 BAD_REQUEST           1000400  Invalid input                           (various errors)
     *  409 CONFLICT              1000901  Constraint validation error             (various errors)
     *  412 PRECONDITION_FAILED   1001400  Asset is stale
     *  500 INTERNAL_SERVER_ERROR 1000100  Error                                   (various errors)
     *  503 SERVICE_UNAVAILABLE   1001100  Error accessing database                (various DB errors)
     *  503 SERVICE_UNAVAILABLE   1000700  Unable to access system                 (various network errors)
     *  503 SERVICE_UNAVAILABLE   3000100  Failed to submit MDU for distribution   (various errors related to MDU)
     */
    submitXML() {
      this.submittingXML = true
      let assetMetadataURI =
        utils.getServerUrl() + "/api/xml-backlog/asset"
      assetMetadataURI += "/" + this.selectedXMLUID
      axios
        .put(assetMetadataURI, {
          assetMetadata: this.assetMetadata,
          xml: this.modifiedXML
        })
        .then((response) => {
          let assetMetadata = response.data.data.assetMetadata
          this.submittingXML = false
          this.editingStep = 3
          this.xmlSubmitSuccess = true
          this.stepsEditable = false
          this.xmlSubmitResponseMsg = this.getXMLSubmitSuccessMsg(assetMetadata)
        })
        .catch((error) => {
          let response = error.response.data
          const statusCode = response.statusCode
          this.submittingXML = false
          this.editingStep = 3
          this.xmlSubmitSuccess = false
          this.xmlSubmitResponseMsg = this.getXMLSubmitFailureMsg(response)

          switch (statusCode) {
            case 1001400:
              // Disable editing any steps, user needs to re-load the XML
              this.stepsEditable = false
              this.isXMLStale = true
              break
            default:
              //  Keep the steps editable, so that user can go back and retry
              this.stepsEditable = true
          }
        })
    },
    /**
     * Get version number
     */
    getVersionNumber(assetMetadata) {
      return assetMetadata.versionMajor + "." + assetMetadata.versionMinor
    },
    /**
     * Construct XML submit success message
     */
    getXMLSubmitSuccessMsg(assetMetadata) {
      //  TODO:Find a better way to pass parameters to messages and style them
      let newVersion = this.getVersionNumber(assetMetadata)
      newVersion = "<b>" + newVersion + "</b>"
      let deliveryId = assetMetadata.deliveryId
      let successMsg =
        messages.info.SUBMIT_XML_SUCCESS +
        newVersion +
        "<br/>" +
        messages.label.ADI_FILE_NAME +
        assetMetadata.adiFileName
      if (!this.$lodash.isNull(deliveryId)) {
        successMsg += "<br/>" + messages.label.DELIVERY_ID + deliveryId
      }
      return successMsg
    },
    /**
     * Construct XML submit failure message
     */
    getXMLSubmitFailureMsg(response) {
      const statusCode = response.statusCode
      let deliveryId
      let failureMsg
      // Check if 'delivryId' exists
      if (this.$lodash.has(response, ["data", "assetMetadata", "deliveryId"])) {
        deliveryId = response.data.assetMetadata.deliveryId
      }
      switch (statusCode) {
        case 1001400:
          failureMsg = messages.error.STALE_XML_ON_SUBMIT
          break
        case 1000900:
          failureMsg = messages.error.XML_VALIDATION
          break
        default:
          failureMsg = messages.error.SUBMIT_XML_FAILURE
      }
      // Add 'delivery Id' to the message if one exists
      if (
        !this.$lodash.isNull(deliveryId) &&
        !this.$lodash.isUndefined(deliveryId)
      ) {
        failureMsg += "<br/>" + messages.label.DELIVERY_ID + deliveryId
      }

      // Show additional details
      if (
        !this.$lodash.isUndefined(response.message) &&
        !this.$lodash.isEmpty(response.message)
      ) {
        failureMsg += "<br/>" + messages.label.DETAILS + response.message
      }

      return failureMsg
    },
    /**
     * Reload XML if we have run in to stale XML scenario
     * After successful retrieval of new XML, upate search results with reflect
     * new XML data
     */
    reloadXML() {
      let newXmlUid = this.assetMetadata.latestXmlUid
      this.selectedXMLUID = newXmlUid
      this.retrieveAssetMetadata(newXmlUid, true)
    },
    /**
     * Reload search results in the background
     */
    refreshSearchResult() {
      this.editorDialog = false
      if (this.xmlSubmitSuccess || this.isXMLStale) {
        this.isXMLStale = false
        XMLBacklogService.$emit("event:refreshSearchResult")
      }
    }
  },
  computed: {
    editorHeight() {
      switch (this.$vuetify.breakpoint.name) {
        case "xs":
          return "65vh"
        case "sm":
          return "65vh"
        case "md":
          return "68vh"
        case "lg":
          return "68vh"
        case "xl":
          return "80vh"
      }
    },
    didSubmitFail() {
      return this.editingStep === 3 && !this.xmlSubmitSuccess
    }
  },
  created() {
    /**
     * Non reactive properties
     */
    this.messages = messages
    /**
     * Events
     */
    XMLBacklogService.$on("event:loadAssetMetadata", (xmlUID) => {
      this.selectedXMLUID = xmlUID
      this.retrieveAssetMetadata(xmlUID)
    })
  }
}
</script>

<style>
.vop--ace-editor {
  position: absolute !important;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
}

.vop--ace-editor .ace_search_field,
.ace_searchbtn:last-child {
  color: black !important;
}

.vop--acediff-editor-container {
  display: flex;
  display: -webkit-flex;
  flex-direction: row;
  position: absolute;
  bottom: 0;
  width: 100%;
  top: 0px !important;
  left: 0px;
  height: 100%;
  overflow: auto;
}

.vop--acediff-editor-container>div {
  flex-grow: 1;
  -webkit-flex-grow: 1;
  position: relative;
}

.vop--acediff-editor-container>div.vop--acediff-gutter {
  flex: 0 0 60px;
  -webkit-flex: 0 0 60px;
  border-left: 1px solid #999999;
  border-right: 1px solid #999999;
  background-color: #efefef;
  overflow: hidden;
}

.vop--acediff-gutter svg {
  background-color: #efefef;
}

.vop--acediff-editor {
  position: absolute !important;
  top: 0;
  bottom: 0;
  width: 100%;
}

/** acediff specific styles, need to move in to a commmon style sheet */

.acediff-diff {
  background-color: #d8f2ff;
  border-top: 1px solid #a2d7f2;
  border-bottom: 1px solid #a2d7f2;
  position: absolute;
  z-index: 4;
}

.acediff-diff.targetOnly {
  height: 0px !important;
  border-top: 1px solid #a2d7f2;
  border-bottom: 0px;
  position: absolute;
}

.acediff-connector {
  fill: #d8f2ff;
  stroke: #a2d7f2;
}
</style>



// WEBPACK FOOTER //
// src/XMLBacklog/XMLBacklogMetadataEdit.vue